import { useState, useEffect } from 'react';
import { useSupabase } from '../context/SupabaseContext';
import { submitOrder, createReservation, submitReview } from '../api';
import { getAdminOrders as fetchAdminOrders, getAdminReservations as fetchAdminReservations, getDashboardStats } from '../api/admin';
import { CartItem, OrderFormData, ReservationData, FeedbackData, DashboardStats } from '../types';
import { supabase } from '../lib/supabase';

// --- Customer Hooks ---

export const useCreateOrder = () => {
  const { loginCustomer } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placeOrder = async (
    name: string, 
    phone: string, 
    email: string, 
    cart: CartItem[], 
    details: OrderFormData, 
    subtotal: number
  ) => {
    setLoading(true);
    setError(null);
    console.log('[useCreateOrder] Initiating order placement...');
    
    try {
      // 1. Login/Create Customer
      // createOrGetCustomer now throws specific errors if RLS fails
      const customer = await loginCustomer(phone, name, email);
      
      if (!customer || !customer.id) {
        throw new Error('Customer creation failed. Please check your internet connection.');
      }
      
      // 2. Submit order linked to customer
      const order = await submitOrder(customer, cart, details, subtotal);
      
      console.log('[useCreateOrder] Order placed successfully:', order.id);
      return order;
    } catch (err: any) {
      console.error('[useCreateOrder] Error:', err);
      setError(err.message || 'Failed to place order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { placeOrder, loading, error };
};

export const useCreateReservation = () => {
  const { loginCustomer } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bookTable = async (data: ReservationData) => {
    setLoading(true);
    setError(null);
    console.log('[useCreateReservation] Initiating booking...');

    try {
      // 1. Login/Create Customer
      const customer = await loginCustomer(data.phone, data.name, data.email);
      
      if (!customer || !customer.id) {
        throw new Error('Customer profile creation failed. Please try again.');
      }
      
      // 2. Create reservation
      const res = await createReservation(customer, data);
      
      console.log('[useCreateReservation] Booking confirmed:', res.id);
      return res;
    } catch (err: any) {
      console.error('[useCreateReservation] Error:', err);
      setError(err.message || 'Failed to book table');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { bookTable, loading, error };
};

export const useSubmitReview = () => {
  const { currentCustomer } = useSupabase();
  const [loading, setLoading] = useState(false);

  const postReview = async (data: FeedbackData) => {
    setLoading(true);
    try {
      const review = await submitReview(currentCustomer, data);
      return review;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { postReview, loading };
};

export const useRealtimeOrders = () => {
  const { currentCustomer } = useSupabase();
  const [activeOrders, setActiveOrders] = useState<any[]>([]);

  useEffect(() => {
    if (!currentCustomer) return;

    // Initial fetch
    supabase
      .from('orders')
      .select('*')
      .eq('customer_id', currentCustomer.id)
      .in('status', ['pending', 'preparing', 'ready'])
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setActiveOrders(data);
      });

    // Realtime subscription
    const subscription = supabase
      .channel('public:orders')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'orders',
        filter: `customer_id=eq.${currentCustomer.id}` 
      }, (payload) => {
         if (payload.eventType === 'INSERT') {
            setActiveOrders(prev => [payload.new, ...prev]);
         } else if (payload.eventType === 'UPDATE') {
            setActiveOrders(prev => prev.map(o => o.id === payload.new.id ? payload.new : o));
         }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [currentCustomer]);

  return { activeOrders };
};

// --- Admin Hooks ---

export const useAdminRealtimeOrders = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    fetchAdminOrders().then(data => {
        setOrders(data || []);
        setLoading(false);
    });
  };

  useEffect(() => {
    refresh();

    const subscription = supabase
      .channel('admin:orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
         refresh(); // Re-fetch to get joined customer data
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  return { orders, loading, refresh };
};

export const useAdminRealtimeReservations = () => {
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    fetchAdminReservations().then(data => {
        setReservations(data || []);
        setLoading(false);
    });
  };

  useEffect(() => {
    refresh();

    const subscription = supabase
      .channel('admin:reservations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations' }, () => {
         refresh();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  return { reservations, loading, refresh };
};

export const useAdminDashboardStats = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        getDashboardStats().then(data => {
            setStats(data);
            setLoading(false);
        });
    };

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 10000); // Polling every 10s
        return () => clearInterval(interval);
    }, []);

    return { stats, loading, refresh };
};